import { skipToken, useSuspenseQuery } from '@apollo/client';
import { useDeferredValue, useMemo } from 'react';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { GetSimilarJobsDocument } from '@xing-com/crate-jobs-graphql';
import type { SimilarJobData } from '@xing-com/crate-jobs-helpers';

const LI_CONSUMER = 'loggedin.web.jobs.posting.similar';
const LO_CONSUMER = 'loggedout.web.jobs.posting.similar';

type SimilarJobsResponse = {
  loading: boolean;
  similarJobs: SimilarJobData[];
};

type Props = { limit: number; jobId?: string; skip?: boolean };

export const useSimilarJobs = ({
  limit,
  jobId,
  skip = false,
}: Props): SimilarJobsResponse => {
  const { isLoggedIn } = useLoginState();

  const variables = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      jobId: jobId as string,
      limit,
      consumer: isLoggedIn ? LI_CONSUMER : LO_CONSUMER,
    }),
    [jobId, limit, isLoggedIn]
  );
  const deferredVariables = useDeferredValue(variables);

  const { error, data } = useSuspenseQuery(
    GetSimilarJobsDocument,
    skip || !jobId
      ? skipToken
      : {
          variables: deferredVariables,
          errorPolicy: 'all',
        }
  );

  if (error && !data) return { loading: false, similarJobs: [] };

  const similarJobs = (data?.viewer?.similarJobs?.collection ?? []).reduce<
    SimilarJobData[]
  >((arr, collectionItem): SimilarJobData[] => {
    if (collectionItem.__typename !== 'JobRecommendationsSimilarJob')
      return arr;

    const { job, trackingToken, position } = collectionItem;

    if (job?.__typename === 'VisibleJob')
      return [
        ...arr,
        {
          job,
          trackingToken,
          position,
          originalPosition: position,
        },
      ];

    return arr;
  }, []);

  return {
    loading: deferredVariables !== variables, // if the deferred variables are not the same as the current variables, it means that the query is still loading with the new variables
    similarJobs,
  };
};
